<template>
    <div v-show="stepActive" class="tournament-details">
        <h2 class="title">{{ $t('title_TournamentDetails') }}</h2>
        <p class="subtitle">{{ $t('subtitle_TournamentDetails') }}</p>

        <div class="info-card">
            <!-- Error State -->
            <v-alert v-if="configError" type="error" outlined>
                <div class="d-flex align-center">
                    <div>
                        <div class="text-h6">{{ $t('error.configurationFailed') || 'Configuration Error' }}</div>
                        <div>{{ $t('error.contactSupport') || 'Unable to load tournament configuration. Please contact Swiset support for assistance.' }}</div>
                    </div>
                </div>
            </v-alert>

            <v-form v-if="!configError">
                <!-- Tournament Type Section -->
                <div class="section">
                    <h3 class="section-title mb-0">{{ $t('tournamentType_TournamentDetails.title') }}</h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1">{{ $t('tournamentType_TournamentDetails.description') }}</p>
                    <v-radio-group v-model="tournamentType" @change="onTournamentTypeChange">
                        <v-radio v-for="type in allowedTypes" :key="type" :value="type">
                            <template v-slot:label>
                                <div class="radio-label">
                                    <div class="d-flex align-center">
                                        {{ $t(`tournamentType_TournamentDetails.${type.toLowerCase()}.label`) }}
                                    </div>
                                    <span class="subtitle-2 text--disabled">
                                        {{ $t(`tournamentType_TournamentDetails.${type.toLowerCase()}.description`) }}
                                    </span>
                                </div>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </div>

                <!-- Platform Section -->
                <div class="section" v-if="tournamentType">
                    <h3 class="section-title mb-0">{{ $t('platform_TournamentDetails.title') }}</h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1">{{ $t('platform_TournamentDetails.description') }}</p>
                    <v-select
                        v-model="selectedPlatform"
                        :items="availablePlatforms"
                        item-text="platformName"
                        item-value="platformCode"
                        outlined
                        dense
                        append-icon="fa-regular fa-chevron-down"
                        @change="onPlatformChange">
                    </v-select>
                </div>

                <!-- Platform Group Section -->
                <div class="section" v-if="selectedPlatform">
                    <h3 class="section-title mb-0">{{ $t('platformGroup_TournamentDetails.title') }}</h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1">{{ $t('platformGroup_TournamentDetails.description') }}</p>
                    <v-select
                        v-if="availableGroups.length > 0"
                        v-model="selectedGroup"
                        :items="availableGroups"
                        item-text="groupName"
                        :item-value="getGroupValue"
                        :return-object="true"
                        outlined
                        dense
                        append-icon="fa-regular fa-chevron-down"
                        @change="onGroupChange">
                    </v-select>
                    <v-text-field
                        v-else
                        value="No groups available"
                        outlined
                        dense
                        readonly
                        disabled>
                    </v-text-field>
                </div>

                <!-- Account Management Section (for platforms with autoEnroll support) -->
                <div class="section" v-if="showAutoEnrollOption">
                    <h3 class="section-title mb-0">{{ $t('accountManagement_TournamentDetails.title') }}</h3>
                    <p class="subtitle-2 text--disabled mb-2 mt-1">{{ $t('accountManagement_TournamentDetails.description') }}</p>
                    <v-radio-group v-model="accountManagementOption" @change="onAccountManagementChange">
                        <v-radio value="swiset">
                            <template v-slot:label>
                                <span v-html="getSwisetManagedLabel"></span>
                            </template>
                        </v-radio>
                        <!---- <v-radio value="self">
                            <template v-slot:label>
                                <span v-html="getSelfManagedLabel"></span>
                            </template>
                        </v-radio> -->
                    </v-radio-group>
                </div>

                <!-- Visibility Section -->
                <!-- <div class="section mb-6">
                    <h3 class="section-title">{{ $t('visibility_TournamentDetails.title') }}</h3>
                    <v-radio-group v-model="visibility" @change="validateForm">
                        <v-radio value="PRIVATE">
                            <template v-slot:label>
                                <div class="radio-label">
                                    <div class="d-flex align-center">
                                        {{ $t('visibility_TournamentDetails.private.label') }}
                                    </div>
                                    <span class="subtitle-2 text--disabled">
                                        {{ $t('visibility_TournamentDetails.private.description') }}
                                    </span>
                                </div>
                            </template>
                        </v-radio>
                        <v-radio disabled value="PUBLIC">
                            <template v-slot:label>
                                <div class="radio-label">
                                    <div class="d-flex align-center">
                                        {{ $t('visibility_TournamentDetails.public.label') }}
                                        <v-chip small class="ml-2 unavailable-chip">
                                            {{ $t('visibility_TournamentDetails.notAvailable') }}
                                        </v-chip>
                                    </div>
                                    <span class="subtitle-2 text--disabled">
                                        {{ $t('visibility_TournamentDetails.public.description') }}
                                    </span>
                                </div>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </div> -->

                <!-- Public Performance Section -->
                <div class="section mb-0">
                    <div>
                        <h3 class="section-title mb-0">{{ $t('performance_TournamentDetails.title') }}</h3>
                    </div>
                    <div class="d-flex justify-space-between">
                        <p class="subtitle-2 text--disabled mt-1">
                            {{ $t('performance_TournamentDetails.description') }}
                        </p>
                        <v-switch
                            class="public-performance-switch"
                            style="margin-top: -2px;"
                            v-model="publicPerformance"
                            @change="validateForm">
                        </v-switch>
                    </div>
                </div>
            </v-form>
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import TournamentRepository from '@/repositories/tournament';

export default {
    name: 'TournamentDetails',
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        },
        clubId: {
            type: [Number, String],
            default: null
        }
    },
    data: () => ({
        wizardConfig: null,
        allowedTypes: [],
        demoPlatforms: [],
        livePlatforms: [],
        demoGroupsByPlatform: {},
        liveGroupsByPlatform: {},
        loading: false,
        configError: false
    }),
    computed: {
        ...mapState({
            formData: state => state.tournamentForm.details,
            stateClubId: state => state.clubMember?.club?.id,
            // Get selectedClubId from basicInfo form state (set in TournamentBasicInfo step)
            selectedClubIdFromBasicInfo: state => state.tournamentForm?.basicInfo?.selectedClubId
        }),
        // Use selectedClubId from form state first, then prop clubId, then fall back to state
        // This ensures we use the user's selection from TournamentBasicInfo dropdown
        computedClubId() {
            return this.selectedClubIdFromBasicInfo || this.clubId || this.stateClubId;
        },
        tournamentType: {
            get() {
                return this.formData.tournamentType || '';
            },
            set(value) {
                this.updateTournamentDetails({
                    ...this.formData,
                    tournamentType: value,
                    platform: '',
                    platformGroup: '',
                    platformGroupId: null
                });
            }
        },
        selectedPlatform: {
            get() {
                return this.formData.platform;
            },
            set(value) {
                this.updateTournamentDetails({
                    ...this.formData,
                    platform: value,
                    platformGroup: '',
                    platformGroupId: null,
                    selfManaged: false
                });
            }
        },
        selectedGroup: {
            get() {
                // Return the selected group object
                if (!this.formData.selectedGroupObj) return null;
                return this.formData.selectedGroupObj;
            },
            set(value) {
                // Value is now the entire group object
                if (!value) {
                    this.updateTournamentDetails({
                        ...this.formData,
                        platformGroup: '',
                        platformGroupId: null,
                        mtGroupId: null,
                        selectedGroupObj: null
                    });
                    return;
                }

                // Store the appropriate IDs based on what's available
                this.updateTournamentDetails({
                    ...this.formData,
                    platformGroup: value.groupName || '',
                    platformGroupId: value.platformGroupId || null,
                    mtGroupId: value.mtGroupId || null,
                    selectedGroupObj: value
                });
            }
        },
        visibility: {
            get() {
                return this.formData.visibility;
            },
            set(value) {
                this.updateTournamentDetails({
                    ...this.formData,
                    visibility: value
                });
            }
        },
        publicPerformance: {
            get() {
                return this.formData.publicPerformance;
            },
            set(value) {
                this.updateTournamentDetails({
                    ...this.formData,
                    publicPerformance: value ? 1 : 0
                });
            }
        },
        accountManagementOption: {
            get() {
                // Map: swiset = autoEnroll false, self = autoEnroll true
                if (this.formData.autoEnroll === true) {
                    return 'self';
                }
                return 'swiset';
            },
            set(value) {
                // swiset = autoEnroll false, self = autoEnroll true
                const isSwiset = value === 'swiset';
                this.updateTournamentDetails({
                    ...this.formData,
                    autoEnroll: !isSwiset
                });
            }
        },
        showAutoEnrollOption() {
            // Show the option only if the selected platform has autoEnroll in extraParams
            if (!this.selectedPlatform || !this.tournamentType) return false;

            const platforms = this.tournamentType === 'DEMO' ? this.demoPlatforms : this.livePlatforms;
            const platform = platforms.find(p => p.platformCode === this.selectedPlatform);

            return platform && platform.extraParams && platform.extraParams.includes('autoEnroll');
        },
        availablePlatforms() {
            if (!this.tournamentType || !this.wizardConfig) return [];

            if (this.tournamentType === 'DEMO') {
                return this.demoPlatforms;
            } else if (this.tournamentType === 'LIVE') {
                return this.livePlatforms;
            }
            return [];
        },
        availableGroups() {
            if (!this.selectedPlatform || !this.tournamentType) return [];

            let groups = [];
            if (this.tournamentType === 'DEMO') {
                groups = this.demoGroupsByPlatform[this.selectedPlatform] || [];
            } else if (this.tournamentType === 'LIVE') {
                groups = this.liveGroupsByPlatform[this.selectedPlatform] || [];
            }

            // Club 209: only show server group 30
            if (this.computedClubId === 209) {
                groups = groups.filter(g => g.platformGroupId === 30 || g.mtGroupId === 30);
            }

            // If groups exist, prepend a "No group selected" option
            if (groups.length > 0) {
                const noGroupOption = {
                    groupName: this.$t('platformGroup_TournamentDetails.noGroupSelected') || 'No group selected',
                    platformGroupId: null,
                    mtGroupId: null,
                    isNoGroupOption: true
                };
                return [noGroupOption, ...groups];
            }

            return groups;
        },
        getSwisetManagedLabel() {
            const provider = this.$t('accountManagement_TournamentDetails.swisetManaged.provider');
            const label = this.$t('accountManagement_TournamentDetails.swisetManaged.label');
            // Replace provider name with styled version
            return label.replace(provider, `<span style="color: var(--v-mainGreen-base); font-weight: 500;">${provider}</span>`);
        },
        getSelfManagedLabel() {
            const provider = this.$t('accountManagement_TournamentDetails.selfManaged.provider');
            const label = this.$t('accountManagement_TournamentDetails.selfManaged.label');
            // Replace provider name with bold version
            return label.replace(provider, `<strong>${provider}</strong>`);
        }
    },
    methods: {
        ...mapMutations(['updateTournamentDetails']),

        getGroupValue(item) {
            // Return a unique identifier for the group
            return item.mtGroupId || item.platformGroupId || item.groupName;
        },


        async fetchWizardConfiguration() {
            this.loading = true;
            this.configError = false;
            try {
                // Log warning if clubId is not available
                if (!this.computedClubId) {
                    console.warn('TournamentDetails: No clubId available for wizard configuration. selectedClubIdFromBasicInfo:', this.selectedClubIdFromBasicInfo, ', clubId prop:', this.clubId, ', stateClubId:', this.stateClubId);
                }
                const response = await TournamentRepository.getWizardConfiguration(this.computedClubId);
                this.wizardConfig = response.data;

                // Update component data with fetched configuration
                this.allowedTypes = this.wizardConfig.allowedTypes || [];
                this.demoPlatforms = this.wizardConfig.demoPlatforms || [];
                this.livePlatforms = this.wizardConfig.livePlatforms || [];
                this.demoGroupsByPlatform = this.wizardConfig.demoGroupsByPlatform || {};
                this.liveGroupsByPlatform = this.wizardConfig.liveGroupsByPlatform || {};

                // Set default tournament type if not already set
                if (!this.tournamentType && this.allowedTypes.length > 0) {
                    this.tournamentType = this.allowedTypes[0];
                }

                // Set default platform if not already set
                if (!this.selectedPlatform && this.availablePlatforms.length > 0) {
                    this.selectedPlatform = this.availablePlatforms[0].platformCode;
                    this.onPlatformChange();
                }

                this.validateForm();
            } catch (error) {
                /* eslint-disable no-console */
                console.error('Error fetching wizard configuration:', error);
                /* eslint-enable no-console */

                // Set error state instead of fallback
                this.configError = true;
                this.allowedTypes = [];
                this.demoPlatforms = [];
                this.livePlatforms = [];
                this.demoGroupsByPlatform = {};
                this.liveGroupsByPlatform = {};

                // Disable the form
                this.setNextBtnDisabled(true);
            } finally {
                this.loading = false;
            }
        },

        onTournamentTypeChange() {
            // Reset group when tournament type changes
            this.selectedGroup = '';

            // Set first available platform as default
            if (this.availablePlatforms.length > 0) {
                this.selectedPlatform = this.availablePlatforms[0].platformCode;
                this.onPlatformChange();
            } else {
                this.selectedPlatform = '';
            }

            this.validateForm();
        },

        onPlatformChange() {
            // Reset group when platform changes
            this.selectedGroup = null;
            if (this.formData.autoEnroll === undefined || this.formData.autoEnroll === null) {
                this.updateTournamentDetails({
                    ...this.formData,
                    autoEnroll: false
                });
            }

            // If no groups available, clear all group-related fields
            if (this.availableGroups.length === 0) {
                this.updateTournamentDetails({
                    ...this.formData,
                    platformGroup: '',
                    platformGroupId: null,
                    mtGroupId: null,
                    selectedGroupObj: null
                });

                // Store platform info with null group IDs
                this.$store.commit('updateTournamentPlatformInfo', {
                    platform: this.selectedPlatform,
                    tournamentType: this.tournamentType,
                    platformGroup: '',
                    platformGroupId: null,
                    mtGroupId: null
                });
            } else {
                // Groups available - automatically select "No group selected" option
                const noGroupOption = this.availableGroups.find(g => g.isNoGroupOption);
                if (noGroupOption) {
                    this.selectedGroup = noGroupOption;
                }

                // Store platform info for use in other steps
                this.$store.commit('updateTournamentPlatformInfo', {
                    platform: this.selectedPlatform,
                    tournamentType: this.tournamentType
                });
            }

            this.validateForm();
        },

        onGroupChange() {
            this.validateForm();

            // Store complete platform info for use in other steps
            this.$store.commit('updateTournamentPlatformInfo', {
                platform: this.selectedPlatform,
                tournamentType: this.tournamentType,
                platformGroup: this.formData.platformGroup,
                platformGroupId: this.formData.platformGroupId,
                mtGroupId: this.formData.mtGroupId
            });
        },

        onAccountManagementChange() {
            this.validateForm();
        },

        validateForm() {
            // Allow continuing if platform has no groups
            const isValid = this.tournamentType &&
                          this.selectedPlatform &&
                          (this.availableGroups.length === 0 || this.selectedGroup) &&
                          this.visibility;

            this.setNextBtnDisabled(!isValid);
        }
    },
    async created() {
        await this.fetchWizardConfiguration();
    }
}
</script>

<style lang="scss" scoped>
.tournament-details {
    width: 100%;

    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        color: var(--v-textPrimary-base);
        font-family: "Avenir", sans-serif !important;
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 60px 60px 42px 60px;
        overflow-y: auto;
        max-height: 60vh;

        .v-form {
            max-width: 720px;
            margin: auto;
        }

        .section {
            margin-bottom: 32px;

            .section-title {
                font-size: 20px;
                font-weight: 500;
                margin-bottom: 16px;
                color: var(--v-textPrimary-base);
            }

            .public-performance-switch {
                .v-input__control {
                    .v-input__slot {
                        .v-label {
                            color: var(--v-mainBlack-base);
                        }
                    }
                }
            }
        }

        .subtitle-2 {
            font-family: "Avenir", sans-serif !important;
        }

        .radio-label {
            display: flex;
            flex-direction: column;
            color: var(--v-mainBlack-base);
            font-family: "Avenir", sans-serif;

            .subtitle-2 {
                margin-top: 4px;
                font-size: 12px;
                font-family: "Avenir", sans-serif !important;
            }
        }

        .unavailable-chip {
            background-color: transparent;
            color: #9A9A9A !important;
            border: 1px solid #9A9A9A;
            font-size: 14px;
        }
    }
}

// Custom switch styling
::v-deep .v-input--switch {
    .v-input--selection-controls__input {
        .v-input--switch__track {
            height: 24px !important;
            width: 45px !important;
            top: calc(50% - 12px) !important;
            border-radius: 12px !important;
        }

        .v-input--switch__thumb {
            height: 20px !important;
            margin-left: 5px;
            width: 20px !important;
            top: calc(50% - 10px) !important;
        }
    }

    // Adjust margins for label alignment
    .v-input--selection-controls__input {
        margin-right: 18px;
    }

    // Adjust thumb position when switched on
    &.v-input--is-active {
        .v-input--switch__thumb {
            transform: translate(20px) !important;
        }

        // Add green color when active
        .v-input--switch__track {
            background-color: var(--v-mainGreen-base) !important;
            opacity: 1 !important;
        }
    }
}

::v-deep .public-performance-switch {
    .v-input--selection-controls__label {
        color: var(--v-mainBlack-base) !important;
    }

    .v-label.theme--light {
        color: var(--v-mainBlack-base) !important;
    }
}

::v-deep .v-input__icon--append .v-icon {
    font-size: 14px;
    color: #9A9A9A;
}

::v-deep .v-list--dense .v-list-item .v-list-item__title {
    font-size: 16px !important;
}
</style>